import { HelpCircle, ArrowRight } from "lucide-react";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { FAQS_SOUTH_FLORIDA_POOL_REMODELING_GUIDE } from "@/data/southFloridaPoolRemodelingGuideData";

/** Sections 7–10 (South Florida factors, proposal, checklist, FAQs) + CTA. */
export function BodyPart3() {
  const faqs = FAQS_SOUTH_FLORIDA_POOL_REMODELING_GUIDE;

  return (
    <>
      {/* Section 7: South Florida factors */}
      <section
        id="south-florida-factors-worth-planning-around"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          South Florida Factors Worth Planning Around
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>
            South Florida&rsquo;s weather and year-round pool use make certain
            planning questions especially important.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Sun and surface temperature
          </h3>
          <p>
            Material, color, texture, time of day, moisture, and exposure can
            all influence how a deck feels underfoot.
          </p>
          <p>
            Avoid assuming that every product within one material category will
            perform identically. Compare actual samples and consider where the
            strongest sun reaches the property.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Rain and drainage
          </h3>
          <p>
            A pool deck must manage rainfall, splash-out, roof runoff,
            irrigation, and water moving from surrounding areas.
          </p>
          <p>
            Drainage should be considered before deck elevations or materials
            are changed&mdash;not after water begins collecting beside a door,
            structure, or newly installed surface.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Water chemistry and finish care
          </h3>
          <p>
            Interior-finish performance is affected by more than the finish
            name. Proper installation, startup, water balance, and ongoing care
            all matter.
          </p>
          <p>
            The National Plasterers Council identifies water chemistry as an
            important factor in the long-term durability and appearance of
            cementitious pool finishes. Responsibilities for filling, startup,
            brushing, water testing, and ongoing care should be understood
            before the remodeled pool returns to normal use.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">
            Material variation
          </h3>
          <p>
            Natural stone and manufactured pavers can both show variation,
            though the type and extent differ by product.
          </p>
          <p>
            Reviewing more than one isolated sample can help homeowners
            understand how color, veining, texture, and pattern may appear
            across a larger deck.
          </p>

          <h3 className="text-xl font-bold text-[#0B1F3D]">Year-round use</h3>
          <p>
            Because South Florida pools are often used through much of the year,
            think beyond how the project looks immediately after completion.
          </p>
          <p>Consider:</p>
          <ul>
            <li>Walking comfort</li>
            <li>Furniture placement</li>
            <li>Shade</li>
            <li>Cleaning</li>
            <li>Entertaining</li>
            <li>Children or frequent guests</li>
            <li>Connections to doors and outdoor rooms</li>
            <li>How the pool area is actually used from week to week</li>
          </ul>
        </div>
      </section>

      {/* Section 8: proposal */}
      <section
        id="what-should-a-pool-remodeling-proposal-clarify"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          What Should a Pool-Remodeling Proposal Clarify?
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>A useful proposal lets you understand what you are comparing.</p>
          <p>Ask whether it clearly addresses:</p>
          <ul>
            <li>Interior-finish type and preparation</li>
            <li>Waterline-tile scope</li>
            <li>Coping material and profile</li>
            <li>Deck square footage and material</li>
            <li>Demolition and debris removal</li>
            <li>Base or substrate preparation</li>
            <li>Drainage-related work</li>
            <li>Fittings and visible accessories</li>
            <li>Verified features or design changes</li>
            <li>Material allowances</li>
            <li>Possible hidden conditions</li>
            <li>Access and property protection</li>
            <li>Startup and care responsibilities</li>
            <li>Change-order procedures</li>
            <li>Exclusions and owner responsibilities</li>
          </ul>
          <p>
            Two proposals with different totals may not describe the same
            project. One may include tile, coping, demolition, preparation, or
            deck work that the other leaves separate.
          </p>
          <p>
            Compare scope first and price second. Otherwise, the least expensive
            proposal can become the most confusing one once work begins.
          </p>
        </div>
      </section>

      {/* Section 9: checklist */}
      <section
        id="pool-remodeling-planning-checklist"
        className="scroll-mt-28 py-6 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-4">
          Pool-Remodeling Planning Checklist
        </h2>
        <div className="prose prose-lg max-w-none text-foreground leading-relaxed space-y-4">
          <p>Before discussing the project, gather the following:</p>
          <ol>
            <li>Photos of the pool, deck, coping, and problem areas</li>
            <li>A list of recurring concerns</li>
            <li>The approximate pool and deck dimensions, if available</li>
            <li>A list of components you hope to keep</li>
            <li>A list of components you want to change</li>
            <li>Examples of styles or materials you like</li>
            <li>Notes about access through the side yard or home</li>
            <li>
              Information about future patios, landscaping, kitchens, pergolas,
              fencing, or other outdoor plans
            </li>
            <li>Questions about material care and appearance</li>
            <li>
              A realistic distinction between necessary work and optional
              upgrades
            </li>
          </ol>
          <p>
            You do not need to diagnose the pool or design the finished project
            yourself. This information simply makes the first conversation more
            productive.
          </p>
        </div>
      </section>

      {/* Section 10: FAQs */}
      <section
        id="frequently-asked-questions"
        className="scroll-mt-28 py-8 border-t border-border"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-6 flex items-center gap-2">
          <HelpCircle className="w-7 h-7 text-[#00A3D9]" />
          Frequently Asked Questions
        </h2>
        <div className="space-y-6">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="bg-card border border-border rounded-xl p-6 shadow-xs"
            >
              <h3 className="text-lg font-bold text-[#0B1F3D] mb-2 flex items-start gap-2">
                <span className="text-[#00A3D9] font-extrabold">Q:</span>
                <span>{faq.question}</span>
              </h3>
              <p className="text-base text-muted-foreground leading-relaxed pl-6">
                {faq.answer}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* In-Article CTA Box */}
      <div className="my-12 bg-[#0B1F3D] text-white rounded-2xl p-8 md:p-10 shadow-xl relative overflow-hidden">
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Planning a Pool Remodel in South Florida?
          </h3>
          <p className="text-white/80 mb-4 text-base md:text-lg">
            Precision Pavers &amp; Pool Design can discuss your pool&rsquo;s
            current condition, the components you are considering updating, and
            how the pool relates to the surrounding deck and outdoor space.
          </p>
          <p className="text-white/80 mb-8 text-base md:text-lg">
            The goal is not to make every project larger. It is to define a
            coordinated scope that fits the property, the existing conditions,
            and what you want the finished space to accomplish.
          </p>
          <QuoteModal>
            <Button
              size="lg"
              className="h-auto w-full sm:w-auto px-6 sm:px-8 py-4 text-base sm:text-lg whitespace-normal bg-[#D4A84B] hover:bg-[#D4A84B]/90 text-white font-semibold rounded-md shadow-lg"
            >
              Discuss Your Pool Remodeling Project
              <ArrowRight className="ml-2 w-5 h-5 shrink-0" />
            </Button>
          </QuoteModal>
        </div>
      </div>
    </>
  );
}
